import { Box, Stack, Typography } from "@mui/material";
import { formatDuration } from "../../utils/formatting";

export interface DurationComparisonProps {
  durationMs: number;
  p50Ms: number;
  p90Ms: number;
  p95Ms: number;
}

function clamp(v: number, min: number, max: number) {
  return Math.max(min, Math.min(max, v));
}

function classify(durationMs: number, p50Ms: number, p90Ms: number, p95Ms: number) {
  if (durationMs <= p50Ms) return "ltP50";
  if (durationMs <= p90Ms) return "p50ToP90";
  if (durationMs <= p95Ms) return "p90ToP95";
  return "gtP95";
}

export function DurationComparison({ durationMs, p50Ms, p90Ms, p95Ms }: DurationComparisonProps) {
  const max = Math.max(p95Ms * 1.5, durationMs * 1.1, 1);

  const xP50 = (p50Ms / max) * 100;
  const xP90 = (p90Ms / max) * 100;
  const xP95 = (p95Ms / max) * 100;
  const xDur = (durationMs / max) * 100;

  const cls = classify(durationMs, p50Ms, p90Ms, p95Ms);
  const color =
    cls === "ltP50"
      ? "success.main"
      : cls === "p50ToP90"
        ? "warning.main"
        : cls === "p90ToP95"
          ? "warning.dark"
          : "error.main";

  const percentileText =
    cls === "ltP50"
      ? "< p50"
      : cls === "p50ToP90"
        ? "p50–p90"
        : cls === "p90ToP95"
          ? "p90–p95"
          : "> p95";

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Stack spacing={1}>
        <Typography variant="body2" sx={{ opacity: 0.8 }}>
          This span took <b>{formatDuration(durationMs)}</b> ({percentileText})
        </Typography>

        <Box
          sx={{
            position: "relative",
            height: 14,
            borderRadius: 999,
            backgroundColor: "divider",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: `linear-gradient(90deg,
                rgba(46, 204, 113, 0.35) 0%,
                rgba(46, 204, 113, 0.35) ${clamp(xP50, 0, 100)}%,
                rgba(241, 196, 15, 0.35) ${clamp(xP50, 0, 100)}%,
                rgba(241, 196, 15, 0.35) ${clamp(xP90, 0, 100)}%,
                rgba(230, 126, 34, 0.35) ${clamp(xP90, 0, 100)}%,
                rgba(230, 126, 34, 0.35) ${clamp(xP95, 0, 100)}%,
                rgba(231, 76, 60, 0.35) ${clamp(xP95, 0, 100)}%,
                rgba(231, 76, 60, 0.35) 100%)`,
            }}
          />

          {/* Markers */}
          {[
            { x: xP50, label: "p50" },
            { x: xP90, label: "p90" },
            { x: xP95, label: "p95" },
          ].map((m) => (
            <Box
              key={m.label}
              title={`${m.label}: ${formatDuration(m.label === "p50" ? p50Ms : m.label === "p90" ? p90Ms : p95Ms)}`}
              sx={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: `${clamp(m.x, 0, 100)}%`,
                width: 2,
                backgroundColor: "text.secondary",
                opacity: 0.8,
              }}
            />
          ))}

          {/* Current duration marker */}
          <Box
            title={`duration: ${formatDuration(durationMs)}`}
            sx={{
              position: "absolute",
              top: -2,
              bottom: -2,
              left: `${clamp(xDur, 0, 100)}%`,
              width: 4,
              borderRadius: 999,
              backgroundColor: color,
            }}
          />
        </Box>

        <Stack direction="row" spacing={2} sx={{ opacity: 0.75 }}>
          <Typography variant="caption">p50: {formatDuration(p50Ms)}</Typography>
          <Typography variant="caption">p90: {formatDuration(p90Ms)}</Typography>
          <Typography variant="caption">p95: {formatDuration(p95Ms)}</Typography>
        </Stack>
      </Stack>
    </Box>
  );
}
